"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Award, Clock, Calendar, Shield, Zap } from "lucide-react"
import type { GoogleReviewsData } from "@/lib/google-reviews"
import HeroReviewsCard, { type HeroReview } from "@/components/hero-reviews-card"

const FALLBACK_REVIEWS: HeroReview[] = [
  { author_name: "Sarah M.", text: "Fast, professional, and fixed my fridge same day. Highly recommend!" },
  { author_name: "James T.", text: "Honest pricing and great communication. My dryer works like new." },
  { author_name: "Linda K.", text: "The technician was on time, friendly, and knew exactly what to do." },
]

function RatingStars({ rating, size = "h-4 w-4" }: { rating: number; size?: string }) {
  return (
    <div className="flex gap-0.5" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, rating - i))
        return (
          <div key={i} className={`relative ${size}`}>
            <svg className={`${size} fill-white/25`} viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <svg className={`${size} fill-yellow-400`} viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function HeroSection({ reviewsData }: { reviewsData: GoogleReviewsData | null }) {
  const reviewData = reviewsData
  const reviews: HeroReview[] =
    reviewsData && reviewsData.reviews.length > 0 ? reviewsData.reviews : FALLBACK_REVIEWS
  const displayRating = reviewData?.rating.toFixed(1) || "5.0"
  const displayReviewCount = reviewData?.totalReviews || 87
  const ratingValue = reviewData?.rating || 5

  return (
    <section className="hero-section bg-gradient-to-br from-red-950 via-red-800 to-red-700 text-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-16">
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center">
          {/* Headline - order-1 on mobile keeps it at top */}
          <div className="mb-4 sm:mb-6 flex flex-col items-center justify-center order-1">
            <h1 className="hero-title text-[2.43rem] sm:text-[2rem] md:text-[2.7rem] lg:text-[3.375rem] font-bold tracking-tight leading-tight text-center mx-auto">
              <span className="block text-white">Expert Appliance</span>{" "}
              <span className="hero-subtitle block text-red-300 mt-1">Repair Services</span>
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center w-full max-w-md mx-auto mb-6 order-2 md:order-4">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white border-2 border-red-500/50 hover:border-red-400 flex items-center justify-center gap-2 transition-all duration-300 px-8 py-4 text-lg sm:text-xl shadow-xl hover:shadow-2xl hover:shadow-red-500/40 font-bold rounded-xl group animate-pulse-subtle"
            >
              <a href="tel:+17744200347">
                <Calendar
                  className="h-5 w-5 group-hover:rotate-12 transition-transform duration-300"
                  aria-hidden="true"
                />
                <span className="font-bold tracking-wide">CALL US NOW</span>
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-900 border-2 border-white hover:border-gray-200 transition-all duration-300 px-8 py-4 text-lg sm:text-xl shadow-xl hover:shadow-2xl hover:shadow-white/30 font-semibold rounded-xl"
            >
              <Link href="/request-service">MAKE AN APPOINTMENT</Link>
            </Button>
          </div>

          <div className="w-full flex justify-center mb-6 order-3 px-2">
            <HeroReviewsCard
              reviews={reviews}
              rating={ratingValue}
              displayRating={displayRating}
              reviewCount={displayReviewCount}
              reviewsUrl="https://maps.app.goo.gl/Bard1tmv8vKQDBtq8"
            />
          </div>

          <div className="flex justify-center mb-6 w-full max-w-4xl mx-auto px-2 sm:px-0 order-4 md:order-2">
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:flex md:flex-wrap gap-2 sm:gap-2.5 md:gap-3 w-full md:justify-center">
              <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full px-4 sm:px-3 md:px-4 py-2.5 sm:py-2 border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap">
                <Award
                  className="h-5 w-5 sm:h-4 sm:w-4 mr-2.5 sm:mr-2 text-yellow-400 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="text-[0.9375rem] sm:text-sm font-medium">10+ Years Experience</span>
              </div>
              <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full px-4 sm:px-3 md:px-4 py-2.5 sm:py-2 border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap">
                <Zap className="h-5 w-5 sm:h-4 sm:w-4 mr-2.5 sm:mr-2 text-blue-400 flex-shrink-0" aria-hidden="true" />
                <span className="text-[0.9375rem] sm:text-sm font-medium">Fast Response</span>
              </div>
              <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full px-4 sm:px-3 md:px-4 py-2.5 sm:py-2 border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap">
                <Clock
                  className="h-5 w-5 sm:h-4 sm:w-4 mr-2.5 sm:mr-2 text-green-400 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="text-[0.9375rem] sm:text-sm font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full px-4 sm:px-3 md:px-4 py-2.5 sm:py-2 border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap">
                <Shield
                  className="h-5 w-5 sm:h-4 sm:w-4 mr-2.5 sm:mr-2 text-purple-400 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="text-[0.9375rem] sm:text-sm font-medium">90-Day Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
